/**
 * Temporary workaround for wheel-picker visual selection drift.
 *
 * Problem:
 * - When users scroll past min/max, the wheel component can keep an internal
 *   scroll position showing an out-of-bounds value (especially with very few
 *   options like the meridiem wheel). This makes the UI appear to select an
 *   invalid value even though the component's committed state remains valid.
 *
 * What this hook does:
 * - Allows wheels to display a transient invalid selection value and then snaps
 *   back to the last valid value.
 * - Revert timing is configurable per field: immediate or deferred until wheel
 *   scrolling becomes idle (debounced via a passive 'wheel' listener).
 * - We opt-in the wheel listener only for low-option wheels (e.g., meridiem)
 *   where aggressive scroll can cause post-revert flicker.
 *
 * Where used:
 * - TimePicker: hour/minute use immediate revert; meridiem uses deferred revert
 *   with opt-in wheel listener.
 * - DatePicker (segmented/composite): immediate revert on invalid selections.
 *
 * Removal plan:
 * - Once the wheel-picker library exposes a reliable onScrollEnd API or fixes
 *   internal selection re-centering with min/max bounds, delete this hook and
 *   its usages.
 *
 * Note:
 * - DOM targeting relies on `[data-rwp]:nth-child(n)` columns under the wrapper.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

type WheelField = string;

export interface UseWheelTempSelectionOptions<TField extends WheelField> {
	/** Fields that should defer clearing temp value until wheel stops. */
	fieldsWithDeferredRevert?: ReadonlyArray<TField>;
}

interface TryCommitParams<TField extends WheelField> {
	field: TField;
	/** Returns true when the candidate value is allowed by constraints. */
	isValid: () => boolean;
	/** Called when value is valid. Should update external state and emit. */
	commit: () => void;
	/** Optional value to show on the wheel while invalid. */
	displayValue?: string;
}

export interface UseWheelTempSelectionResult<TField extends WheelField> {
	/** Per-field temporary override value for the wheel’s 'value' prop. */
	tempValueByField: Partial<Record<TField, string>>;
	/**
	 * Attach a debounced wheel listener to a specific wheel column.
	 * Returns a cleanup function; should be called inside a React effect.
	 */
	attachWheelStopListener: (
		rootEl: HTMLElement | null,
		wheelIndex: number,
		field: TField,
	) => () => void;
	/** Commit value if valid; otherwise set a temp display value and schedule revert. */
	tryCommit: (params: TryCommitParams<TField>) => void;
	/** Explicitly clear a field’s temp override. */
	clearTemp: (field: TField) => void;
}

export function useWheelTempSelection<TField extends WheelField>(
	options?: UseWheelTempSelectionOptions<TField>,
): UseWheelTempSelectionResult<TField> {
	const deferred = options?.fieldsWithDeferredRevert ?? [];
	const [tempValueByField, setTempValueByField] = useState<
		Partial<Record<TField, string>>
	>({});

	// Track whether a wheel is actively scrolling for a field.
	const isWheelActiveRef = useRef(new Map<TField, boolean>());
	// Debounce timers per field
	const wheelTimersRef = useRef(new Map<TField, number>());

	const clearTemp = useCallback((field: TField) => {
		setTempValueByField((prev) => {
			if (prev[field] === undefined) return prev;
			return { ...prev, [field]: undefined };
		});
	}, []);

	const setTempForField = useCallback((field: TField, value: string) => {
		setTempValueByField((prev) => ({ ...prev, [field]: value }));
	}, []);

	const scheduleWheelIdle = useCallback(
		(field: TField) => {
			const timers = wheelTimersRef.current;
			const maybeExisting = timers.get(field);
			if (maybeExisting) window.clearTimeout(maybeExisting);
			const id = window.setTimeout(() => {
				isWheelActiveRef.current.set(field, false);
				// When wheel becomes idle, clear temp (if any)
				clearTemp(field);
			}, 100);
			timers.set(field, id);
		},
		[clearTemp],
	);

	const attachWheelStopListener: UseWheelTempSelectionResult<TField>['attachWheelStopListener'] =
		useCallback(
			(rootEl, wheelIndex, field) => {
				const wheelEl = rootEl?.parentElement?.querySelector(
					`[data-rwp]:nth-child(${wheelIndex})`,
				) as HTMLDivElement | null;
				if (!wheelEl) return () => {};

				const handleWheel = () => {
					isWheelActiveRef.current.set(field, true);
					scheduleWheelIdle(field);
				};

				wheelEl.addEventListener('wheel', handleWheel, { passive: true });
				return () => {
					wheelEl.removeEventListener('wheel', handleWheel);
					const timer = wheelTimersRef.current.get(field);
					if (timer) window.clearTimeout(timer);
				};
			},
			[scheduleWheelIdle],
		);

	const tryCommit = useCallback(
		({ field, isValid, commit, displayValue }: TryCommitParams<TField>) => {
			const ok = isValid();
			if (!ok) {
				// keep the wheel visually in sync with user’s scroll
				if (displayValue !== undefined) setTempForField(field, displayValue);

				const shouldDefer = deferred.includes(field);
				const isWheelActive = isWheelActiveRef.current.get(field) === true;
				if (!shouldDefer || !isWheelActive) {
					// immediate revert on next frame
					requestAnimationFrame(() => clearTemp(field));
				}
				return;
			}

			// valid → clear temp and commit
			clearTemp(field);
			commit();
		},
		[clearTemp, deferred, setTempForField],
	);

	return useMemo(
		() => ({ tempValueByField, attachWheelStopListener, tryCommit, clearTemp }),
		[attachWheelStopListener, clearTemp, tempValueByField, tryCommit],
	);
}
